import type {
  GroupCollection,
  GroupCollectionListItem,
  GroupDetail,
  GroupListItem,
  GroupPostListItem,
  PostCommentThread,
  PostComment,
  MentionCandidate,
  CreateGroupInput,
  UpdateGroupInput,
  CreateGroupCollectionInput,
  UpdateGroupCollectionInput,
  CreatePostInput,
  CreateCommentInput,
  GroupMemberRole,
  GroupMember,
  GroupRosterMember,
  GroupScope,
  GroupAutoJoinRule,
  FounderStage,
  UpsertAutoJoinRuleInput,
  MentionFeedItem,
  ReplyFeedItem,
} from '@doneverse/shared'
import { api } from '../api/index.js'

export const groupService = {
  // ─── Collections ──────────────────────────────────────────────
  fetchCollections: async (): Promise<GroupCollectionListItem[]> => {
    return api.get('/v1/group-collections').json<GroupCollectionListItem[]>()
  },
  fetchCollectionGroups: async (collectionId: string): Promise<GroupListItem[]> => {
    return api.get(`/v1/group-collections/${collectionId}/groups`).json<GroupListItem[]>()
  },
  createCollection: async (input: CreateGroupCollectionInput): Promise<GroupCollection> => {
    return api.post(input, '/v1/group-collections').json<GroupCollection>()
  },
  updateCollection: async (
    collectionId: string,
    input: UpdateGroupCollectionInput,
  ): Promise<GroupCollection> => {
    return api.patch(input, `/v1/group-collections/${collectionId}`).json<GroupCollection>()
  },
  archiveCollection: async (collectionId: string): Promise<void> => {
    await api.delete(`/v1/group-collections/${collectionId}`).res()
  },

  // ─── Groups ───────────────────────────────────────────────────
  fetchGroups: async (scope: GroupScope = 'mine'): Promise<GroupListItem[]> => {
    return api.get(`/v1/groups?scope=${scope}`).json<GroupListItem[]>()
  },

  fetchGroupDetail: async (groupId: string): Promise<GroupDetail> => {
    return api.get(`/v1/groups/${groupId}`).json<GroupDetail>()
  },

  createGroup: async (input: CreateGroupInput): Promise<GroupDetail> => {
    return api.post(input, '/v1/groups').json<GroupDetail>()
  },

  updateGroup: async (groupId: string, input: UpdateGroupInput): Promise<GroupDetail> => {
    return api.patch(input, `/v1/groups/${groupId}`).json<GroupDetail>()
  },

  archiveGroup: async (groupId: string): Promise<void> => {
    await api.delete(`/v1/groups/${groupId}`).res()
  },

  // ─── Members ──────────────────────────────────────────────────
  /** Full roster (with presence) for the member-management panel. */
  fetchMembers: async (groupId: string): Promise<GroupRosterMember[]> => {
    return api.get(`/v1/groups/${groupId}/members`).json<GroupRosterMember[]>()
  },

  addMember: async (
    groupId: string,
    body: { user_id?: string; role?: GroupMemberRole } = {},
  ): Promise<GroupMember> => {
    return api.post(body, `/v1/groups/${groupId}/members`).json<GroupMember>()
  },

  /** Self-join is just an empty add-member call. */
  joinGroup: async (groupId: string): Promise<GroupMember> => {
    return api.post({}, `/v1/groups/${groupId}/members`).json<GroupMember>()
  },

  /**
   * Remove a member. Addressed by `community_authors.id` (the roster row's
   * `author_id`), NOT `users.id` — members imported from LearnWorlds have no
   * Doneverse account and can only be named by their author id.
   */
  removeMember: async (groupId: string, authorId: string): Promise<void> => {
    await api.delete(`/v1/groups/${groupId}/members/${authorId}`).res()
  },

  /** Change a member's role. Addressed by `author_id`; see removeMember. */
  updateMemberRole: async (
    groupId: string,
    authorId: string,
    role: GroupMemberRole,
  ): Promise<GroupMember> => {
    return api.patch({ role }, `/v1/groups/${groupId}/members/${authorId}`).json<GroupMember>()
  },

  // ─── Posts ────────────────────────────────────────────────────
  fetchGroupPosts: async (
    groupId: string,
    options: { limit?: number; before?: string; includeDeleted?: boolean; q?: string } = {},
  ): Promise<GroupPostListItem[]> => {
    const params = new URLSearchParams()
    if (options.limit !== undefined) params.set('limit', String(options.limit))
    if (options.before) params.set('before', options.before)
    if (options.includeDeleted) params.set('include_deleted', 'true')
    if (options.q) params.set('q', options.q)
    const qs = params.toString()
    const path = qs ? `/v1/groups/${groupId}/posts?${qs}` : `/v1/groups/${groupId}/posts`
    return api.get(path).json<GroupPostListItem[]>()
  },

  createPost: async (groupId: string, input: CreatePostInput): Promise<GroupPostListItem> => {
    return api.post(input, `/v1/groups/${groupId}/posts`).json<GroupPostListItem>()
  },

  updatePost: async (postId: string, content: string): Promise<GroupPostListItem> => {
    return api.patch({ content }, `/v1/posts/${postId}`).json<GroupPostListItem>()
  },

  deletePost: async (postId: string): Promise<void> => {
    await api.delete(`/v1/posts/${postId}`).res()
  },

  togglePin: async (postId: string, pinned: boolean): Promise<GroupPostListItem> => {
    return api.post({ pinned }, `/v1/posts/${postId}/pin`).json<GroupPostListItem>()
  },

  setCommentsDisabled: async (postId: string, disabled: boolean): Promise<GroupPostListItem> => {
    return api.post({ disabled }, `/v1/posts/${postId}/comments-disabled`).json<GroupPostListItem>()
  },

  // ─── Comments ─────────────────────────────────────────────────
  fetchPostComments: async (postId: string): Promise<PostCommentThread[]> => {
    return api.get(`/v1/posts/${postId}/comments`).json<PostCommentThread[]>()
  },

  createComment: async (postId: string, input: CreateCommentInput): Promise<PostComment> => {
    return api.post(input, `/v1/posts/${postId}/comments`).json<PostComment>()
  },

  deleteComment: async (commentId: string): Promise<void> => {
    await api.delete(`/v1/comments/${commentId}`).res()
  },

  // ─── Reactions ────────────────────────────────────────────────
  addReaction: async (postId: string, emoji: string): Promise<void> => {
    await api.post({ emoji }, `/v1/posts/${postId}/reactions`).res()
  },

  removeReaction: async (postId: string, emoji: string): Promise<void> => {
    await api.delete(`/v1/posts/${postId}/reactions/${encodeURIComponent(emoji)}`).res()
  },

  addCommentReaction: async (commentId: string, emoji: string): Promise<void> => {
    await api.post({ emoji }, `/v1/comments/${commentId}/reactions`).res()
  },

  removeCommentReaction: async (commentId: string, emoji: string): Promise<void> => {
    await api.delete(`/v1/comments/${commentId}/reactions/${encodeURIComponent(emoji)}`).res()
  },

  // ─── Polls ────────────────────────────────────────────────────
  /** Cast a single-select vote. Server returns 409 if already voted, 410 if closed/expired. */
  castPollVote: async (contentItemId: string, optionId: string): Promise<void> => {
    await api.post({ option_id: optionId }, `/v1/polls/${contentItemId}/vote`).res()
  },

  /** Close the poll. Author-only (or admin). Idempotent. */
  closePoll: async (contentItemId: string): Promise<void> => {
    await api.post({}, `/v1/polls/${contentItemId}/close`).res()
  },

  // ─── Mention autocomplete ─────────────────────────────────────
  fetchMentionCandidates: async (groupId: string, q: string): Promise<MentionCandidate[]> => {
    const path = `/v1/groups/${groupId}/mention-autocomplete?q=${encodeURIComponent(q)}`
    return api.get(path).json<MentionCandidate[]>()
  },

  // ─── Auto-join rules ─────────────────────────────────────────
  fetchAutoJoinRules: async (groupId: string): Promise<GroupAutoJoinRule[]> => {
    return api.get(`/v1/groups/${groupId}/auto-join-rules`).json<GroupAutoJoinRule[]>()
  },

  upsertFounderStageRule: async (groupId: string, stages: FounderStage[]): Promise<GroupAutoJoinRule> => {
    const body: UpsertAutoJoinRuleInput = {
      rule_type: 'founder_stage',
      rule_value: { stages },
    }
    return api.post(body, `/v1/groups/${groupId}/auto-join-rules`).json<GroupAutoJoinRule>()
  },

  deleteAutoJoinRule: async (groupId: string, ruleId: string): Promise<void> => {
    await api.delete(`/v1/groups/${groupId}/auto-join-rules/${ruleId}`).res()
  },

  // ─── Aggregate feeds (cross-group inbox) ─────────────────────
  fetchUserMentions: async (): Promise<MentionFeedItem[]> => {
    return api.get('/v1/users/me/mentions').json<MentionFeedItem[]>()
  },

  fetchUserReplies: async (): Promise<ReplyFeedItem[]> => {
    return api.get('/v1/users/me/replies').json<ReplyFeedItem[]>()
  },
}
